import { parseSocialEmbedUrl, socialEmbedAspectRatio, socialEmbedIframeSrc, socialEmbedTitle, type SocialEmbed } from "@/lib/social-embeds";
import { parseYouTubeVideoUrl } from "@/lib/youtube-video";

export type ExternalEmbedProvider = "youtube" | "getty" | "vimeo" | "social" | "unknown";
export type ExternalEmbedAspectRatio = "16:9" | "4:3" | "auto";

export type ExternalEmbed = {
  provider: ExternalEmbedProvider;
  embedUrl: string;
  url: string;
  safeHtml?: string;
  renderMode?: "iframe" | "widget";
  anchorId?: string;
  sig?: string;
  itemId?: string;
  aspectRatio?: ExternalEmbedAspectRatio;
  title?: string;
  social?: SocialEmbed;
  videoId?: string;
  width?: number;
  height?: number;
  caption?: boolean;
  tld?: string;
  is360?: boolean;
};

export const UNSUPPORTED_EXTERNAL_EMBED_ERROR = "Nem támogatott embed. Getty, YouTube vagy támogatott közösségi beágyazást adj meg.";

const GETTY_EMBED_HOSTS = new Set(["embed.gettyimages.com"]);
const GETTY_PAGE_HOSTS = new Set(["gettyimages.com", "www.gettyimages.com"]);
const GETTY_WIDGET_HOSTS = new Set(["embed-cdn.gettyimages.com"]);
const VIMEO_PLAYER_HOSTS = new Set(["player.vimeo.com"]);
const VIMEO_PAGE_HOSTS = new Set(["vimeo.com", "www.vimeo.com"]);

function decodeHtmlEntities(input: string) {
  return input
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");
}

export function extractIframeSrc(input: string) {
  return extractIframeAttributes(input)?.src ?? null;
}

function extractIframeAttributes(input: string) {
  if (!/<iframe[\s>]/i.test(input)) return null;
  const match = input.match(/<iframe\b[^>]*>/i);
  if (!match) return { src: "" };
  return {
    src: htmlAttribute(match[0], "src") ?? "",
    width: parseCssPixelValue(htmlAttribute(match[0], "width")),
    height: parseCssPixelValue(htmlAttribute(match[0], "height")),
  };
}

function canonicalUrl(input: string) {
  const trimmed = decodeHtmlEntities(input).trim();
  if (!trimmed || /^javascript:/i.test(trimmed)) return null;
  const candidate = trimmed.startsWith("//")
    ? `https:${trimmed}`
    : /^[a-z][a-z\d+.-]*:/i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    url.protocol = "https:";
    return url;
  } catch {
    return null;
  }
}

function providerLabel(provider: ExternalEmbedProvider) {
  if (provider === "getty") return "Getty Images embed";
  if (provider === "youtube") return "YouTube";
  if (provider === "vimeo") return "Vimeo";
  if (provider === "social") return "Beágyazott tartalom";
  return "Embed";
}

function gettyImageIdFromUrl(url: URL) {
  const host = url.hostname.toLowerCase();
  if (GETTY_EMBED_HOSTS.has(host)) {
    return url.pathname.match(/^\/embed\/(\d+)(?:\/|$)/)?.[1] ?? null;
  }
  if (!GETTY_PAGE_HOSTS.has(host)) return null;
  return url.pathname.match(/(?:^|\/)(\d+)(?:\/)?$/)?.[1]
    ?? url.pathname.match(/picture-id(\d+)(?:$|[/?#])/i)?.[1]
    ?? null;
}

function gettyDetailUrl(id: string) {
  return `https://www.gettyimages.com/detail/${encodeURIComponent(id)}`;
}

function parseCssPixelValue(input: string | null | undefined) {
  if (!input) return undefined;
  const value = Number(input.trim().replace(/px$/i, ""));
  if (!Number.isFinite(value) || value <= 0) return undefined;
  return Math.round(value);
}

function htmlAttribute(tag: string, name: string) {
  const match = tag.match(new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, "i"));
  return match ? decodeHtmlEntities(match[1] ?? match[2] ?? match[3] ?? "").trim() : null;
}

function gettyWidgetConfigString(input: string, key: string) {
  const match = input.match(new RegExp(`\\b${key}\\s*:\\s*(["'])(.*?)\\1`, "i"));
  return match?.[2]?.trim() ?? null;
}

function gettyWidgetConfigBoolean(input: string, key: string) {
  const match = input.match(new RegExp(`\\b${key}\\s*:\\s*(true|false)\\b`, "i"));
  return match ? match[1].toLowerCase() === "true" : undefined;
}

function safeGettyAnchorId(input: string | null | undefined) {
  if (!input) return undefined;
  const value = input.trim();
  return /^[a-zA-Z][\w:-]{0,120}$/.test(value) ? value : undefined;
}

function safeGettyTld(input: string | null | undefined) {
  if (!input) return undefined;
  const value = input.trim().toLowerCase();
  return /^[a-z]{2,12}$/.test(value) ? value : undefined;
}

function safeGettySig(input: string | null | undefined) {
  if (!input) return undefined;
  const value = input.trim();
  return value.length <= 240 && /^[^<>"'\s]+$/.test(value) ? value : undefined;
}

function gettyDebug(message: string, payload?: unknown) {
  if (process.env.NODE_ENV !== "production") console.info(`[GettyEmbed] ${message}`, payload ?? "");
}

function hasOnlyGettyWidgetScripts(input: string) {
  const scriptTags = input.match(/<script\b[\s\S]*?<\/script>/gi) ?? [];
  if (!scriptTags.length) return true;

  for (const tag of scriptTags) {
    const src = htmlAttribute(tag, "src");
    if (src) {
      const url = canonicalUrl(src.startsWith("//") ? `https:${src}` : src);
      if (!url || !GETTY_WIDGET_HOSTS.has(url.hostname.toLowerCase()) || url.pathname !== "/widgets.js") return false;
      continue;
    }

    const body = tag.replace(/^<script\b[^>]*>/i, "").replace(/<\/script>$/i, "");
    if (!/\b(?:window\.)?gie\b/.test(body) || !/gie\.widgets\.load\s*\(/.test(body)) return false;
  }

  return true;
}

function parseGettyWidgetSnippet(input: string): ExternalEmbed | null {
  if (!/<(?:a|script)\b/i.test(input)) return null;
  if (!/(?:gie-single|embed-cdn\.gettyimages\.com\/widgets\.js|gie\.widgets\.load|gettyimages\.com\/detail)/i.test(input)) return null;
  if (!hasOnlyGettyWidgetScripts(input)) return null;

  const anchorMatch = input.match(/<a\b[^>]*>/i);
  const href = anchorMatch ? htmlAttribute(anchorMatch[0], "href") : null;
  const hrefUrl = href ? canonicalUrl(href) : null;
  const idFromHref = hrefUrl ? gettyImageIdFromUrl(hrefUrl) : null;
  const idFromItems = gettyWidgetConfigString(input, "items");
  const id = idFromItems && /^\d+$/.test(idFromItems) ? idFromItems : idFromHref;
  if (!id || !/^\d+$/.test(id)) return null;

  const anchorId = safeGettyAnchorId(gettyWidgetConfigString(input, "id")) ?? safeGettyAnchorId(anchorMatch ? htmlAttribute(anchorMatch[0], "id") : null);
  const sig = safeGettySig(gettyWidgetConfigString(input, "sig"));
  const width = parseCssPixelValue(gettyWidgetConfigString(input, "w"));
  const height = parseCssPixelValue(gettyWidgetConfigString(input, "h"));
  const caption = gettyWidgetConfigBoolean(input, "caption");
  const tld = safeGettyTld(gettyWidgetConfigString(input, "tld"));
  const is360 = gettyWidgetConfigBoolean(input, "is360");
  gettyDebug("parsed Getty config", { anchorId, sig, itemId: id, width, height, caption, tld, is360 });

  return {
    provider: "getty",
    url: hrefUrl?.toString() ?? gettyDetailUrl(id),
    embedUrl: gettyDetailUrl(id),
    ...(anchorId ? { anchorId } : {}),
    ...(sig ? { sig } : {}),
    itemId: id,
    aspectRatio: "4:3",
    title: providerLabel("getty"),
    renderMode: "widget",
    ...(width ? { width } : {}),
    ...(height ? { height } : {}),
    ...(typeof caption === "boolean" ? { caption } : {}),
    ...(tld ? { tld } : {}),
    ...(typeof is360 === "boolean" ? { is360 } : {}),
  };
}

function parseGetty(url: URL, iframeAttrs?: { width?: number; height?: number } | null): ExternalEmbed | null {
  const id = gettyImageIdFromUrl(url);
  if (!id) return null;
  const isIframeEmbed = GETTY_EMBED_HOSTS.has(url.hostname.toLowerCase());
  const caption = url.searchParams.get("caption") === "true" ? true : url.searchParams.get("caption") === "false" ? false : undefined;
  const sig = isIframeEmbed ? safeGettySig(url.searchParams.get("sig")) : undefined;
  const tld = isIframeEmbed ? safeGettyTld(url.searchParams.get("tld")) : undefined;
  return {
    provider: "getty",
    url: isIframeEmbed ? gettyDetailUrl(id) : url.toString(),
    embedUrl: isIframeEmbed ? url.toString() : gettyDetailUrl(id),
    itemId: id,
    aspectRatio: "4:3",
    title: providerLabel("getty"),
    ...(isIframeEmbed ? { renderMode: "iframe" as const } : { renderMode: "widget" as const }),
    ...(sig ? { sig } : {}),
    ...(iframeAttrs?.width ? { width: iframeAttrs.width } : {}),
    ...(iframeAttrs?.height ? { height: iframeAttrs.height } : {}),
    ...(typeof caption === "boolean" ? { caption } : {}),
    ...(tld ? { tld } : {}),
  };
}

function parseVimeo(url: URL): ExternalEmbed | null {
  const host = url.hostname.toLowerCase();
  if (VIMEO_PLAYER_HOSTS.has(host)) {
    const videoId = url.pathname.match(/^\/video\/(\d+)/)?.[1];
    if (!videoId) return null;
    return {
      provider: "vimeo",
      url: `https://vimeo.com/${encodeURIComponent(videoId)}`,
      embedUrl: `https://player.vimeo.com/video/${encodeURIComponent(videoId)}`,
      aspectRatio: "16:9",
      title: providerLabel("vimeo"),
    };
  }

  if (!VIMEO_PAGE_HOSTS.has(host)) return null;
  const videoId = url.pathname.match(/^\/(\d+)/)?.[1];
  if (!videoId) return null;
  return {
    provider: "vimeo",
    url: `https://vimeo.com/${encodeURIComponent(videoId)}`,
    embedUrl: `https://player.vimeo.com/video/${encodeURIComponent(videoId)}`,
    aspectRatio: "16:9",
    title: providerLabel("vimeo"),
  };
}

function sanitizeIframeHtml(embed: ExternalEmbed) {
  const allow = embed.provider === "youtube" || embed.provider === "vimeo"
    ? "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    : "fullscreen; clipboard-write";
  const sandbox = "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox";
  return `<iframe src="${embed.embedUrl.replace(/"/g, "&quot;")}" title="${(embed.title ?? providerLabel(embed.provider)).replace(/"/g, "&quot;")}" loading="lazy" referrerpolicy="strict-origin-when-cross-origin" sandbox="${sandbox}" allow="${allow}" allowfullscreen></iframe>`;
}

export function parseExternalEmbed(input: string): ExternalEmbed | null {
  const iframeAttrs = extractIframeAttributes(input);
  const rawInput = iframeAttrs !== null ? iframeAttrs.src : input;
  const gettyWidget = iframeAttrs === null ? parseGettyWidgetSnippet(input) : null;
  if (gettyWidget) return gettyWidget;

  const youtube = parseYouTubeVideoUrl(rawInput);
  if (youtube) {
    const embed: ExternalEmbed = {
      provider: "youtube",
      url: youtube.url,
      embedUrl: youtube.embedUrl,
      aspectRatio: "16:9",
      title: providerLabel("youtube"),
      videoId: youtube.videoId,
    };
    return { ...embed, safeHtml: sanitizeIframeHtml(embed) };
  }

  const social = parseSocialEmbedUrl(iframeAttrs !== null ? rawInput : input);
  if (social) {
    const embedUrl = socialEmbedIframeSrc(social.embedUrl, 920);
    const embed: ExternalEmbed = {
      provider: "social",
      url: social.url,
      embedUrl,
      aspectRatio: socialEmbedAspectRatio({ ...social, embedUrl }) === "4 / 3" ? "4:3" : "auto",
      title: socialEmbedTitle(social),
      social: { ...social, embedUrl },
    };
    return { ...embed, safeHtml: sanitizeIframeHtml(embed) };
  }

  const url = canonicalUrl(rawInput);
  if (!url) return null;
  const parsed = parseGetty(url, iframeAttrs) ?? parseVimeo(url);
  if (!parsed) return null;
  return parsed.provider === "getty" ? parsed : { ...parsed, safeHtml: sanitizeIframeHtml(parsed) };
}

export function externalEmbedProviderLabel(embed: Pick<ExternalEmbed, "provider" | "title"> | null | undefined) {
  return embed?.title || providerLabel(embed?.provider ?? "unknown");
}

export function externalEmbedAspectRatioCss(embed: Pick<ExternalEmbed, "aspectRatio" | "provider" | "width" | "height">) {
  if ("width" in embed && "height" in embed && typeof embed.width === "number" && typeof embed.height === "number" && embed.width > 0 && embed.height > 0) {
    return `${embed.width} / ${embed.height}`;
  }
  if (embed.aspectRatio === "4:3") return "4 / 3";
  if (embed.aspectRatio === "auto") return embed.provider === "getty" ? "4 / 3" : "1 / 1";
  return "16 / 9";
}
